/* Copyright (c) 2020-present CloudIO Inc */

import { getManager } from '../../registry/Manager';
import { Platform } from '../../platform/Platform';
import { baseComponentManager } from '../../registry/Components';
import {
  ComponentManagerFn,
  RequiredItem,
} from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { defaultValue, preCompile } from './LocalDataStore';
import {
  controlledBy,
  displayName,
  dsAppUidProp,
  getNextAlias,
  hideDirtyIndicator,
} from '../DataStore/DataStoreManager';
import { camelCase } from 'lodash-es';

const LocalDataStoreManager: ComponentManagerFn<'LocalDataStore'> = (
  platform: Platform,
) => {
  let timer: NodeJS.Timeout;
  return {
    ...baseComponentManager<'LocalDataStore'>(platform, 'LocalDataStore'),
    type: 'LocalDataStore',
    name: 'Local Data Store',
    isChildAllowed: (page, item, childType) =>
      (['TriggerBeforeCreate', 'TriggerOnInitialize'] as ItemType[]).includes(
        childType,
      ),
    defaultValue,
    props: {
      dsAppUid: dsAppUidProp(platform),
      dataSource: {
        label: 'Data Source',
        type: 'string',
        required: true,
      },
      alias: {
        label: 'Data Source Alias',
        type: 'DataSourceAlias',
        required: true,
      },
      displayName,
      dataFormat: {
        label: 'Data Format',
        type: 'select',
        options: { csv: 'CSV', json: 'JSON', ts: 'Typescript' },
        required: true,
      },
      data: {
        label: 'CSV Data',
        type: 'csv',
        required: (item: RequiredItem<'LocalDataStore'>) =>
          item.props.dataFormat === 'csv',
      },
      jsonData: {
        label: 'JSON Data',
        type: 'json',
        required: (item: RequiredItem<'LocalDataStore'>) =>
          item.props.dataFormat === 'json',
      },
      tsData: {
        label: 'Typescript',
        type: 'typescript',
        required: (item: RequiredItem<'LocalDataStore'>) =>
          item.props.dataFormat === 'ts',
        defaultScript: `
return [
    {
        firstName: "John",
        number: 123.456,
        dateTime: new Date().toISOString(),
        boolean: true,
        checkbox: "Y"
    }
];
`,
        preCompile,
      },
      discardInitialData: {
        label: 'Discard Initial Data',
        type: 'boolean',
        helpText:
          'Check this to NOT populate the data store with initial data.',
      },
      insertable: {
        label: 'Insertable',
        type: 'booleanExpression',
      },
      updatable: {
        label: 'Updatable',
        type: 'booleanExpression',
      },
      deletable: {
        label: 'Deletable',
        type: 'boolean',
      },
      singleton: {
        label: 'Singleton',
        type: 'boolean',
        helpText:
          'Singleton dataStores defined across different pages, with the same alias, will share the same state.',
      },
      ignorePKDuplicate: {
        label: 'Ignore Primary Keys Duplicates',
        type: 'boolean',
        helpText:
          'Check this to ignore the duplicate primary key values on this store.',
      },
      controlledBy,
      hideDirtyIndicator,
    },
    isPropertyVisible: (property, item) => {
      if (property === 'dsAppUid') {
        return false;
      }
      switch (property) {
        case 'controlledBy':
          return false;
        case 'dataSource':
          return true;
        case 'alias':
          return !!item.dataSource;
        case 'jsonData':
          return !!item.alias && item.dataFormat === 'json';
        case 'tsData':
          return !!item.alias && item.dataFormat === 'ts';
        case 'data':
          return !!item.alias && item.dataFormat === 'csv';
        default:
          return !!item.alias;
      }
    },
    onPropertyChange: (property, value, itemProps, { appUid, pageId }) => {
      if (property === 'dataSource' && typeof value === 'string') {
        const map = platform.getDataSources(appUid, pageId);
        const val = getNextAlias(`${camelCase(value)}Alias`, map);
        if (timer) {
          clearTimeout(timer);
        }
        timer = setTimeout(() => {
          platform.resetAliases(appUid, pageId);
        }, 1300);
        return {
          [property]: value,
          alias: val,
        };
      }
      if (property === 'dataFormat' && value !== itemProps.dataFormat) {
        return {
          [property]: value,
          jsonData: '',
          data: '',
          tsData: '',
        };
      }
      return {
        [property]: value,
      };
    },
    onCopy: (props, samePage) => ({
      ...props,
      alias: samePage ? '' : props.alias,
    }),
    getDisplayText: (item, expanded, { appUid, pageId }) => {
      if (!item.props.alias) return '';
      const page = platform.getPage(appUid, pageId);
      const itemIds = Object.keys(page);
      const usage = itemIds.filter((id) => {
        const itm = page[id];
        const mgr = getManager(itm.type);
        return mgr.isDataSourceAliasUsed(itm, item.props.alias);
      });
      return usage.length
        ? `${item.props.alias} (${usage.length} usage${
            usage.length > 1 ? 's' : ''
          })`
        : item.props.alias;
    },
    generateShortId: (item) => item.alias,
    replaceableWith: ['ReadOnlyDataStore', 'DataStore', 'CloudDataStore'],
    category: 'Data',
    icon: { iconName: 'database' },
    description: `Use a Local Data Store for simple static data that can be used in the UI without any dependency on the backend database table.

This will be useful during the initial page design or while creating mockups where your data model may not be finalized. Once the data model stabilizes you can convert this data store to a regular store backed by a database table with the same structure.

\`\`\`alert_success
You can generate a Data Source using this CSV once the data structure is finalized.
\`\`\`
`,
  };
};

export default LocalDataStoreManager;
